import { z } from 'zod'
import { salesRatesSchema } from '../dto/sales-price-list.dto.js'
import { salesItemKindSchema } from '../enums/sales-item-kind.enum.js'
import { packagePriceErrors, seasonErrors } from '../helpers/sales-prices.js'
import { calendarDaySchema } from '../validators/temporal.validator.js'

/**
 * A venue's price list, saved whole (docs/sales.spec.md, "The price list"). A
 * season is named by a `key` its package prices refer to: its id when it was
 * saved before, any other short string for a new one. Items keep their `id`
 * when they had one; an item or season left out is removed.
 */

const name = z.string().trim().min(1, 'Give it a name.').max(120, 'That name is too long.')

const seasonInputSchema = z
  .object({
    key: z.string().min(1).max(64),
    name,
    startsOn: calendarDaySchema,
    endsOn: calendarDaySchema,
  })
  .strict()

const packagePriceInputSchema = z
  .object({
    /** The season's `key`; null for any other date. */
    seasonKey: z.string().min(1).max(64).nullable(),
    weekdays: z.array(z.number().int().min(0).max(6)).max(7),
    priceCents: z.number().int().min(0, 'A price can’t be negative.').max(100_000_000, 'That price is too high.'),
  })
  .strict()

const itemInputSchema = z
  .object({
    id: z.string().uuid().optional(),
    kind: salesItemKindSchema,
    trackId: z.string().uuid().nullable(),
    name,
    description: z.string().max(2000, 'That description is too long.'),
    priceCents: z.number().int().min(0, 'A price can’t be negative.').max(100_000_000, 'That price is too high.').nullable(),
    taxable: z.boolean(),
    serviceCharged: z.boolean(),
    prices: z.array(packagePriceInputSchema).max(40, 'That’s too many prices for one package.'),
  })
  .strict()

export const putSalesPriceListInputSchema = z
  .object({
    rates: salesRatesSchema.strict(),
    seasons: z.array(seasonInputSchema).max(20, 'That’s too many seasons.'),
    items: z.array(itemInputSchema).max(200, 'That’s too many items.'),
  })
  .strict()
  .superRefine((input, ctx) => {
    const fail = (message: string, path: (string | number)[]): void => {
      ctx.addIssue({ code: 'custom', message, path })
    }
    const keys = new Set<string>()
    input.seasons.forEach((s, i) => {
      if (keys.has(s.key)) fail('Two seasons share a key.', ['seasons', i, 'key'])
      keys.add(s.key)
    })
    const itemIds = new Set<string>()
    for (const message of seasonErrors(input.seasons.map((s) => ({ id: s.key, name: s.name, from: s.startsOn, to: s.endsOn })))) {
      fail(message, ['seasons'])
    }
    const seasonName = (key: string | null): string =>
      key === null ? 'any other date' : (input.seasons.find((s) => s.key === key)?.name ?? 'an unknown season')
    input.items.forEach((item, i) => {
      if (item.id !== undefined) {
        if (itemIds.has(item.id)) fail('An item is in the list twice.', ['items', i, 'id'])
        itemIds.add(item.id)
      }
      if (item.kind === 'package') {
        if (item.priceCents !== null) fail(`${item.name} is priced by season, not by one price.`, ['items', i, 'priceCents'])
        item.prices.forEach((row, r) => {
          if (row.seasonKey !== null && !keys.has(row.seasonKey)) fail('That season isn’t in the list.', ['items', i, 'prices', r, 'seasonKey'])
        })
        const rows = item.prices.map((row) => ({ seasonId: row.seasonKey, weekdays: row.weekdays, priceCents: row.priceCents }))
        for (const message of packagePriceErrors(item.name, rows, seasonName)) fail(message, ['items', i, 'prices'])
      } else {
        if (item.priceCents === null) fail(`${item.name} needs a price.`, ['items', i, 'priceCents'])
        if (item.prices.length > 0) fail(`Only a package is priced by season.`, ['items', i, 'prices'])
      }
    })
  })
export type PutSalesPriceListInput = z.infer<typeof putSalesPriceListInputSchema>
